import React, { Fragment, useEffect, useState } from 'react'
import LocalStorage from 'services/LocalStorage'
import { classNames as cn } from 'helpers'
import { superAxiosRequest } from 'axiosApi'
import st from './Settings.module.scss'
import styles from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import FormField from 'components/UI/AsideModal/Form/FormField'
import FormLabel from 'components/UI/AsideModal/Form/FormLabel'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import EditBox from 'components/HeadBox/components/EditBox/EditBox'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import FormFieldset from 'components/UI/AsideModal/Form/FormFieldset'
import {
  HeaderAddButton,
  HeaderButton,
} from 'components/UI/SuperButton/SuperButton'
import useWindowWidth from '../utils/hooks/useWindowWidth'
import AddRole from 'components/ContentBox/Global/GlobalModalSingle/templates/CRM/AddRole/AddRole'
import { aTimezone } from '../utils/helpers/timeZone'
import asideModalStore from 'Stores/asideModalStore'
import { ReactComponent as RuIcon } from 'components/HeadBox/img/ru.svg'
import { ReactComponent as EnIcon } from 'components/HeadBox/img/en.svg'
import { ReactComponent as KzIcon } from 'components/HeadBox/img/kz.svg'
import { ReactComponent as Save } from 'components/CRM/UI/icons/bookmark.svg'
import { ReactComponent as DeleteIcon } from 'components/UI/icons/delete-bin-6-line.svg'
import { ReactComponent as ArrowIcon } from 'components/UI/icons/arrow-down-s-line.svg'
import swal from 'sweetalert'
import { Link } from 'react-router-dom'
import CardsFunnelName from './CardsFunnelName/CardsFunnelName'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import { ReasonChoices } from './ReasonChoices/ReasonChoices'
import useLocalization from 'hooks/useLocalization'
import { ListOfCities } from './ListOfCities/ListOfCities'
import { ListOfLevels } from './ListOfLevels/ListOfLevels'
import { LearningType } from './LearningType/LearningType'
import { TypeOfTraining } from './TypeOfTraining/TypeOfTraining'
import { TrainingDays } from './TrainingDays/TrainingDays'
import { PurposeOfTraining } from './PurposeOfTraining/PurposeOfTraining'
import { SourceList } from './SourceList/SourceList'
import { TagsList } from './TagsList/TagsList'
import { SettingsChoicesManager } from './SettingsChoicesManager/SettingsChoicesManager'

const languages = [
  { text: 'English', code: 'EN', Icon: () => <EnIcon /> },
  { text: 'Русский', code: 'RU', Icon: () => <RuIcon /> },
  { text: 'Казахский', code: 'KZ', Icon: () => <KzIcon /> },
]

const permissionMapping = {
  Tasks: 'users.view_tasks_crm',
  'All tasks': {
    permission: 'users.view_all_tasks_crm',
    parent: 'Tasks',
    isSubPermission: true,
  },
  Deals: 'users.view_deals_crm',
  'Delete Deal': {
    permission: 'users.delete_deals_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'Просмотр всех сделок CRM': 'users.view_all_deals_crm',
  Funnels: 'users.view_funnels_crm',
  Goals: 'users.view_goals_crm',
  Leads: 'users.view_leads_crm',
  "Client's profiles": 'users.view_clients_crm',
  'Employees analytics': 'users.view_employee_analytics_crm',
  'Sales analytics': [
    'users.view_sales_analytics_crm',
    'users.view_detailed_sales_analytics_crm',
  ],
  'Call analytics': ['users.view_calls_crm', 'users.view_calls_analytics_crm'],
  'Basic settings': 'users.view_settings_crm',
  'Settings user': 'users.view_settings_user_crm',
  'Settings chat': 'users.view_settings_chat_crm',
  'Auto reply templates': {
    permission: 'users.edit_autoreply_templates_wazzup_crm',
    parent: 'Settings chat',
    isSubPermission: true,
  },
  'Response templates': {
    permission: 'users.edit_response_templates_wazzup_crm',
    parent: 'Settings chat',
    isSubPermission: true,
  },
  'Deals history': {
    permission: 'users.view_logs_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'View deferred deals': {
    permission: 'users.view_deferred_deals_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'Add deferred deals': {
    permission: 'users.add_deferred_deals_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'Edit deferred deals': {
    permission: 'users.edit_deferred_deals_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'Delete deferred deals': {
    permission: 'users.delete_deferred_deals_crm',
    parent: 'Deals',
    isSubPermission: true,
  },
  'List of events': 'users.view_event_history_crm',
}

const buildPermissionMaps = (mapping) => {
  const parentChildMap = {}
  const childToParentMap = {}

  Object.entries(mapping).forEach(([key, value]) => {
    if (typeof value === 'object' && value.isSubPermission) {
      const parentDisplayName = value.parent
      const parentValue = mapping[parentDisplayName]
      let parentCode

      if (typeof parentValue === 'string') {
        parentCode = parentValue
      } else if (Array.isArray(parentValue)) {
        parentCode = parentValue[0]
      }

      if (parentCode) {
        if (!parentChildMap[parentCode]) {
          parentChildMap[parentCode] = []
        }
        parentChildMap[parentCode].push(value.permission)
        childToParentMap[value.permission] = parentCode
      }
    }
  })
  return { parentChildMap, childToParentMap }
}

const Settings = () => {
  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'crm_settings',
    dict: {
      basic_settings: 'Basic settings',
      heading: 'Heading',
      template_name_placeholder: 'Template name',
      timezone: 'Timezone',
      date_format: 'Date format',
      currency: 'Currency',
      account_address: 'Account address',
      country: 'Country',
      time_format: 'Time format',
      contact_name_order: 'Contact name order',
      save: 'Save',
      localization: 'Localization',
      translator: 'Translator',
      go_to_translator: 'Go to Translator',
      menu_item_settings_for_roles: 'Menu item settings for roles',
      all_users: 'All users',
      add_role: 'Add role',
      funnel_1: 'Funnel 1',
      access: 'Access',
      show_all: 'Show all',
      deal_fields: 'Deal’s fields',
      add_contact: 'Add contact',
      name: 'Name',
      add_funnels: 'Add funnels',
      funnel_name: 'Funnel name',
      add_field: 'Add a field',
      field_name: 'Field name',
      field_type: 'Field type',
      boolean: 'Boolean',
      text: 'Text',
      edit: 'Edit ',
      delete: 'Delete',
      list_of_levels: 'List of levels',
      add_level: 'Add level',
      list_of_cities: 'List of cities',
      add_city: 'Add a city',
      refusal: 'Reason for refusal',
      add_reason: 'Add a reason',

      meeting_format: 'Meeting format',
      add_meeting_format: 'Add meeting format',
      year_of_study: 'Year of study',
      add_year_of_study: 'Add year of study',
      country_of_admission: 'Country of admission',
      add_country_of_admission: 'Add country of admission',
      posttraining_perspectives: 'Post-training perspectives',
      add_posttraining_perspectives: 'Add perspective',
      flow: 'Flow',
      add_flow: 'Add flow',
      program_selection: 'Program selection',
      add_program_selection: 'Add program selection',
    },
  })

  const ud = LocalStorage.get('ud')
  const user_data = ud?.user_data
  const userData = user_data?.profile
  const id = user_data?.id
  const lang = userData?.localization

  const windowWidth = useWindowWidth()
  const {
    modalValues: modalValue,
    changeValue: changeValues,
    check,
  } = asideModalStore

  const [isLanguages, setIsLanguages] = useState(false)
  const [roles, setRoles] = useState([])
  const [expandedRows, setExpandedRows] = useState({})

  const { parentChildMap, childToParentMap } =
    buildPermissionMaps(permissionMapping)

  const updateProfile = (user_data) => {
    const newProfile = { ...ud, user_data }
    LocalStorage.set('ud', newProfile)
    window.location.reload()
  }

  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }))
  }

  const changeLanguage = (lang) => {
    const qJson = { localization: lang }
    const apiConf = { method: 'put', link: `accounts/account_update/${id}` }
    superAxiosRequest(apiConf, qJson).then(({ data }) => {
      updateProfile(data)
      if (data?.user_data?.profile?.localization === lang) {
        document.location.replace(window.location.href)
      }
    })
  }

  useEffect(() => {
    superAxiosRequest({
      link: `accounts/crm_roles`,
      method: 'get',
    })
      .then((response) => {
        const sortedRoles = response.data.sort((a, b) => a.id - b.id)
        setRoles(sortedRoles)
      })
      .catch((error) => console.error(error))
  }, [])

  const handleCheckboxChange = (role, toggledPermissions) => {
    const updatedPermissions = roles.map((r) => {
      if (r.id !== role.id) return r
      let newPermissions = [...r.permissions]
      toggledPermissions.forEach((perm) => {
        // Если переключается дочернее право
        if (childToParentMap[perm]) {
          const parentPerm = childToParentMap[perm]
          if (newPermissions.includes(perm)) {
            // Снятие дочернего права
            newPermissions = newPermissions.filter((p) => p !== perm)
          } else {
            // При включении дочернего автоматически включается родительское
            if (!newPermissions.includes(parentPerm)) {
              newPermissions.push(parentPerm)
            }
            newPermissions.push(perm)
          }
        }
        // Если переключается родительское право, у которого есть дочерние
        else if (parentChildMap[perm]) {
          if (newPermissions.includes(perm)) {
            // Снятие родительского – снимаются и все дочерние
            newPermissions = newPermissions.filter(
              (p) => p !== perm && !parentChildMap[perm].includes(p),
            )
          } else {
            // Включение родительского без дочерних
            newPermissions.push(perm)
          }
        }
        // Обычное переключение для остальных прав
        else {
          if (newPermissions.includes(perm)) {
            newPermissions = newPermissions.filter((p) => p !== perm)
          } else {
            newPermissions.push(perm)
          }
        }
      })
      return { ...r, permissions: newPermissions }
    })

    setRoles(updatedPermissions)
    const roleData = updatedPermissions.find((r) => r.id === role.id)
    superAxiosRequest(
      { link: `accounts/crm_roles/${role.id}`, method: 'put' },
      { id: role.id, name: role.name, permissions: roleData.permissions },
    )
      .then(() => {})
      .catch(() => {})
  }

  const addRole = () => {
    const ms = {
      modalBodyComponent: () => <AddRole />,
      button: translate.save,
      link: 'accounts/crm_roles',
      title: translate.add_contact,
      isCenter: true,
      onSubmitSuccess: (data) => {
        setRoles([...roles, data])
      },
      valid: { name: 'required' },
    }
    asideModalStore.activity(ms)
  }

  const deleteRole = (roleId, roleName) => {
    swal({
      title: 'Are you sure?',
      text: `Do you really want to delete this role? This action cannot be undone. ${roleName}`,
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const apiConf = {
          method: 'delete',
          link: `accounts/crm_roles/${roleId}`,
        }
        superAxiosRequest(apiConf)
          .then(() => {
            const updatedRoles = roles.filter((role) => role.id !== roleId)
            setRoles(updatedRoles)
            swal('The role has been deleted.', {
              icon: 'success',
            })
          })
          .catch(() => {
            swal('Failed to delete the role.')
          })
      }
    })
  }

  return (
    <div className={st.container}>
      {/*Basic settings*/}
      <div className={st.basicSattings}>
        <PageHeader title={translate.basic_settings}>
          {windowWidth <= 425 ? (
            <div onClick={() => {}}>
              <span className={st.basicSattings__saveBtn}>
                <Save />
              </span>
            </div>
          ) : (
            <div className={st.basicSattings__btn} onClick={() => {}}>
              {translate.save}
            </div>
          )}
        </PageHeader>

        <div className={st.basicSattings__box}>
          <FormFieldset>
            <div className={st.basicSattings__wrapper}>
              <FormField>
                <FormLabel>{translate.heading}:</FormLabel>
                <FormInput isError={check('heading')}>
                  <input
                    placeholder={translate.template_name_placeholder}
                    type='text'
                    value={modalValue.heading || ''}
                    onChange={(e) => changeValues(e.target.value, 'heading')}
                  />
                </FormInput>
              </FormField>
              <FormField>
                <FormLabel>{translate.account_address}:</FormLabel>
                <FormInput isError={check('address')}>
                  <input
                    placeholder='Interpress'
                    type='text'
                    value={modalValue.address || ''}
                    onChange={(e) => changeValues(e.target.value, 'address')}
                  />
                </FormInput>
              </FormField>

              <FormField>
                <FormLabel>{translate.timezone}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.timezone}
                    options={aTimezone}
                    sortKey='timezone'
                    valueID={modalValue.timezone}
                    onChange={(o) => changeValues(o.id, 'timezone')}
                    error={check('timezone')}
                  />
                </FormInput>
              </FormField>

              <FormField>
                <FormLabel>{translate.date_format}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.date_format}
                    options={[]}
                    sortKey='date_format'
                    valueID={modalValue.date_format}
                    onChange={(o) => changeValues(o.id, 'date_format')}
                    error={check('date_format')}
                  />
                </FormInput>
              </FormField>
              <FormField>
                <FormLabel>{translate.currency}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.currency}
                    options={[]}
                    sortKey='currency'
                    valueID={modalValue.currency}
                    onChange={(o) => changeValues(o.id, 'currency')}
                    error={check('currency')}
                  />
                </FormInput>
              </FormField>
              <FormField>
                <FormLabel>{translate.country}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.country}
                    options={[]}
                    sortKey='country'
                    valueID={modalValue.time_format}
                    onChange={(o) => changeValues(o.id, 'country')}
                    error={check('country')}
                  />
                </FormInput>
              </FormField>
              <FormField>
                <FormLabel>{translate.time_format}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.time_format}
                    options={[]}
                    sortKey='time_format'
                    valueID={modalValue.time_format}
                    onChange={(o) => changeValues(o.id, 'time_format')}
                    error={check('time_format')}
                  />
                </FormInput>
              </FormField>

              <FormField>
                <FormLabel>{translate.contact_name_order}:</FormLabel>
                <FormInput>
                  <SelectCustom
                    placeholder={translate.contact_name_order}
                    options={[]}
                    sortKey='contact'
                    valueID={modalValue.contact}
                    onChange={(o) => changeValues(o.id, 'contact')}
                    error={check('contact')}
                  />
                </FormInput>
              </FormField>
            </div>
          </FormFieldset>
        </div>
      </div>

      <TagsList />
      <ReasonChoices translate={translate} />
      <LearningType translate={translate} />
      <TypeOfTraining translate={translate} />
      <PurposeOfTraining translate={translate} />
      <TrainingDays translate={translate} />
      <SourceList translate={translate} />

      <ListOfCities translate={translate} />
      <ListOfLevels translate={translate} />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_meeting_format_choices'
        pageTitle={translate.meeting_format}
        addButtonText={translate.add_meeting_format}
        emptyStateText='Нет добавленных форматов встреч'
        modalAddTitle='Добавить формат встречи'
        modalEditTitle='Редактировать формат встречи'
        translate={translate}
      />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_year_of_study_choices'
        pageTitle={'Класс/курс'}
        addButtonText={'Добавить класс/курс'}
        emptyStateText='Нет добавленных классов/курсов'
        modalAddTitle='Добавить класс/курс'
        modalEditTitle='Редактировать класс/курс'
        translate={translate}
      />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_country_of_admission_choices'
        pageTitle={translate.country_of_admission}
        addButtonText={translate.add_country_of_admission}
        emptyStateText='Нет добавленных стран поступления'
        modalAddTitle='Добавить страну поступления'
        modalEditTitle='Редактировать страну поступления'
        translate={translate}
      />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_posttraining_perspectives_choices'
        pageTitle={translate.posttraining_perspectives}
        addButtonText={translate.add_posttraining_perspectives}
        emptyStateText='Нет добавленных перспектив'
        modalAddTitle='Добавить перспективу'
        modalEditTitle='Редактировать перспективу'
        translate={translate}
      />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_flow_choices'
        pageTitle={translate.flow}
        addButtonText={translate.add_flow}
        emptyStateText='Нет добавленных потоков'
        modalAddTitle='Добавить поток'
        modalEditTitle='Редактировать поток'
        translate={translate}
      />

      <SettingsChoicesManager
        apiEndpoint='settings/project_settings/deal_program_selection_choices'
        pageTitle={translate.program_selection}
        addButtonText={translate.add_program_selection}
        emptyStateText='Нет добавленных программ'
        modalAddTitle='Добавить программу'
        modalEditTitle='Редактировать программу'
        translate={translate}
      />

      {/*Localization*/}
      <div className={st.localAndTransl}>
        <div className={st.settingsLocalization}>
          <PageHeader title={translate.localization} />
          <div
            className={st.settingsLocalization__box}
            onClick={() => setIsLanguages(!isLanguages)}
          >
            <p className={st.settingsLocalization__title}>Язык</p>
            <EditBox>
              {languages
                .filter(({ code }) => code === lang)
                .map(({ text, code, Icon }, idx) => (
                  <Fragment key={idx}>
                    <p>{code}</p>
                    <Icon />
                  </Fragment>
                ))}
            </EditBox>
            {isLanguages && (
              <div className={st.settingsLocalization__menu}>
                {languages
                  .filter(({ code }) => code !== lang)
                  .map(({ text, Icon, code }, idx) => (
                    <div
                      key={text}
                      onClick={() => changeLanguage(code)}
                      className={cn({ [st.settingsLocalization__item]: true })}
                    >
                      <Icon />
                      <p>{text}</p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className={st.settingsLocalization}>
          <PageHeader title={translate.translator} />
          <div className={st.settingsLocalization__box}>
            <Link to='/crm/translate' target='_blank'>
              <HeaderButton
                hidePlus={true}
                buttonColor={'red'}
                title={translate.go_to_translator || ''}
                onClick={() => {}}
              />
            </Link>
          </div>
        </div>
      </div>
      {/*ROLES*/}
      <div className={st.settingsRoles}>
        <div className={st.headerContainer}>
          <PageHeader title={translate.menu_item_settings_for_roles}>
            <HeaderAddButton title={translate.add_role} onClick={addRole} />
          </PageHeader>
        </div>
        <div>
          <table
            className={`${styles.table} ${st.table} ${styles.tbodyStriped}`}
          >
            <thead className={`${styles.conversionTbl}`}>
              <tr>
                <td
                  className={`${styles.tdTotal} ${styles.tdMiddle} ${st.td} ${st.role}`}
                >
                  {translate.all_users}
                </td>
                {[...roles]
                  .sort((a, b) => a.id - b.id)
                  .map((role) => (
                    <td
                      key={role.id}
                      className={`${styles.tdTotal} ${styles.textAlignCenter} ${styles.tdMiddle} ${st.rolesBorders} ${st.role}`}
                    >
                      <div className={st.roleColumn}>
                        <span>{role.name} </span>
                        <div
                          className={st.deleteRole}
                          onClick={() => deleteRole(role.id, role.name)}
                        >
                          <DeleteIcon />
                        </div>
                      </div>
                    </td>
                  ))}
              </tr>
            </thead>

            <tbody className={styles.conversionTbl}>
              {Object.entries(permissionMapping)
                // Выводим только родительские права в основной части
                .filter(
                  ([_, value]) =>
                    !(typeof value === 'object' && value.isSubPermission),
                )
                .map(([key, value]) => (
                  <Fragment key={key}>
                    <tr className={`${expandedRows[key] ? styles.opened : ''}`}>
                      <td
                        className={`${styles.tdTotal} ${styles.tdMiddle} ${st.td}`}
                      >
                        <div className={st.tdContent}>
                          <div className={st.table__checkbox}>
                            <span>{key}</span>
                          </div>

                          {/* Рендерим кнопку "show all" у тех прав, что имеют дочерние */}
                          {Object.entries(permissionMapping).some(
                            ([, perm]) =>
                              typeof perm === 'object' &&
                              perm.isSubPermission &&
                              perm.parent === key,
                          ) && (
                            <div
                              className={st.showAllBtn}
                              onClick={() => toggleRow(key)}
                            >
                              {translate.show_all}
                              <ArrowIcon
                                className={`${expandedRows[key] ? st.arrowUp : ''}`}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      {[...roles]
                        .sort((a, b) => a.id - b.id)
                        .map((role, roleIndex) => (
                          <td
                            key={roleIndex}
                            className={`${styles.tdTotal} ${styles.bdrN} ${styles.textAlignCenter} ${styles.tdMiddle} ${st.td}`}
                          >
                            {Array.isArray(value) ? (
                              <input
                                type='checkbox'
                                checked={value.every((perm) =>
                                  role.permissions.includes(perm),
                                )}
                                onChange={() =>
                                  handleCheckboxChange(role, value)
                                }
                              />
                            ) : (
                              <input
                                type='checkbox'
                                checked={role.permissions.includes(value)}
                                onChange={() =>
                                  handleCheckboxChange(role, [value])
                                }
                              />
                            )}
                          </td>
                        ))}
                    </tr>
                    {/* Субправа выводятся только при раскрытии родительской строки */}
                    {expandedRows[key] &&
                      Object.entries(permissionMapping)
                        .filter(
                          ([, perm]) =>
                            perm.isSubPermission && perm.parent === key,
                        )
                        .map(([subKey, subValue]) => (
                          <tr key={subKey} className={st.expandedRow}>
                            <td
                              className={`${styles.tdTotal} ${styles.tdMiddle} ${st.td}`}
                            >
                              <div className={st.table__checkbox}>
                                <span className={st.historyText}>{subKey}</span>
                              </div>
                            </td>
                            {[...roles]
                              .sort((a, b) => a.id - b.id)
                              .map((role, roleIndex) => (
                                <td
                                  key={roleIndex}
                                  className={`${styles.tdTotal} ${styles.bdrN} ${styles.textAlignCenter} ${styles.tdMiddle} ${st.td}`}
                                >
                                  <label>
                                    <input
                                      type='checkbox'
                                      checked={role.permissions.includes(
                                        subValue.permission,
                                      )}
                                      onChange={() =>
                                        handleCheckboxChange(role, [
                                          subValue.permission,
                                        ])
                                      }
                                    />
                                  </label>
                                </td>
                              ))}
                          </tr>
                        ))}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <CardsFunnelName />
    </div>
  )
}

export default Settings
